import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "../data/site";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-black isolate"
    >
      {/* Background video (with poster fallback) */}
      <div className="absolute inset-0 z-0">
        <video
          data-testid="hero-bg-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={SITE.hero}
          className="absolute inset-0 w-full h-full object-cover scale-105 opacity-60"
          style={{ filter: "saturate(1.1) contrast(1.05)" }}
        >
          <source
            src="https://videos.pexels.com/video-files/3327959/3327959-hd_1920_1080_24fps.mp4"
            type="video/mp4"
          />
          <source
            src="https://videos.pexels.com/video-files/4761426/4761426-hd_1920_1080_25fps.mp4"
            type="video/mp4"
          />
        </video>

        {/* Slow-motion feel: subtle zoom drift */}
        <motion.div
          aria-hidden
          className="absolute inset-0"
          initial={{ scale: 1 }}
          animate={{ scale: 1.08 }}
          transition={{ duration: 22, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
        />

        {/* Layered cinematic gradients */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(36,0,70,0.7),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(90,24,154,0.45),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(0,0,0,1),transparent_65%)]" />

        {/* Vignette */}
        <div className="absolute inset-0 shadow-[inset_0_0_180px_60px_rgba(0,0,0,0.95)] pointer-events-none" />

        {/* Subtle grid */}
        <div className="absolute inset-0 grid-lines opacity-30 mix-blend-overlay" />

        {/* Grain */}
        <div className="absolute inset-0 noise pointer-events-none" />
      </div>

      {/* Animated lighting: sweeping spotlights */}
      <motion.div
        aria-hidden
        className="absolute -top-1/3 -left-1/4 w-[80vw] h-[80vw] rounded-full z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(157,78,221,0.35) 0%, rgba(157,78,221,0.08) 35%, transparent 65%)",
          filter: "blur(40px)",
        }}
        animate={{ x: [0, 80, -60, 0], y: [0, 40, -30, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute -bottom-1/3 -right-1/4 w-[70vw] h-[70vw] rounded-full z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,218,31,0.14) 0%, rgba(255,218,31,0.04) 30%, transparent 60%)",
          filter: "blur(60px)",
        }}
        animate={{ x: [0, -90, 50, 0], y: [0, -50, 30, 0] }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 w-full pt-32 pb-24 sm:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-3 mb-6 sm:mb-8"
        >
          <span className="h-px w-10 sm:w-14 bg-brand-yellow" />
          <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-brand-yellow">
            Training the next generation of dance creatives
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-display uppercase text-white leading-[0.85] text-balance"
          style={{ letterSpacing: "0.01em" }}
        >
          <span className="block text-[4rem] sm:text-[7rem] lg:text-[10rem] xl:text-[11rem]">
            USDD
          </span>
          <span className="block text-[3.25rem] sm:text-[6rem] lg:text-[9rem] xl:text-[10rem] text-brand-yellow text-glow-yellow -mt-2">
            Academy
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.32 }}
          className="mt-6 sm:mt-8 font-display text-2xl sm:text-4xl lg:text-5xl text-white tracking-wide flex flex-wrap items-baseline gap-x-3 gap-y-1"
        >
          <span>Train.</span>
          <span className="text-brand-purpleLight">Create.</span>
          <span className="italic text-brand-yellow">Perform.</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-7 max-w-xl sm:max-w-2xl text-base sm:text-lg text-zinc-300/90 leading-relaxed font-light"
        >
          Street dance training, youth development and creative education across
          London &amp; Kent — from weekly classes and performance opportunities
          to qualifications and future BA-level pathways.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-9 sm:mt-11 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
        >
          <Link
            to="/schedule"
            data-testid="hero-book-class-btn"
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover transition-all hover:scale-[1.03] glow-yellow"
          >
            <Calendar size={16} />
            Book a Class
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <a
            href="#journey"
            data-testid="hero-start-journey-btn"
            className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/20 text-white font-bold uppercase text-xs tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow transition-all"
          >
            Start Your Journey
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#contact"
            data-testid="hero-contact-us-btn"
            className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-zinc-300 font-medium uppercase text-xs tracking-[0.2em] hover:text-white transition-all"
          >
            Contact Us
          </a>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-16 sm:mt-20 grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-6 max-w-3xl border-t border-white/10 pt-8 sm:pt-10"
        >
          {[
            { num: "20+", label: "Years Training Dancers" },
            { num: "500+", label: "Students Per Year" },
            { num: "30+", label: "Pro Choreographers" },
            { num: "BA", label: "Pathway · Soon" },
          ].map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-3xl sm:text-4xl text-white tracking-wide">{s.num}</div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-zinc-500 mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 z-10">
        <span className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">Scroll</span>
        <motion.div
          className="h-10 w-px bg-gradient-to-b from-brand-yellow via-brand-yellow/40 to-transparent"
          animate={{ scaleY: [1, 0.5, 1], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "top" }}
        />
      </div>
    </section>
  );
};
